import store from 'customerPath/store'

import ActivityFeedPage from '@/views/ActivityFeedPage'
import CMSPage from '@/views/CMSPage'
import CMSPreview from '@/views/CMSPreview'
import GroupPage from '@/views/GroupPage'
import GroupsPage from '@/views/GroupsPage'
import Language from '@/views/Language'
import NewsPage from '@/views/NewsPage'
import OccasionsPage from '@/views/OccasionsPage'
import ProfilePage from '@/views/ProfilePage'
import RatingPage from '@/views/RatingPage'
import RatingsPage from '@/views/RatingsPage'
import HotelOverviewPage from '@/views/HotelOverviewPage'
import SearchResultsPage from '@/views/SearchResultsPage'
import WhoIsWhoPage from '@/views/WhoIsWhoPage'

// error pages
import AccessDenied from '@/views/errorPages/AccessDenied'
import GeneralError from '@/views/errorPages/GeneralError'
import PageNotFound from '@/views/errorPages/PageNotFound'
import ResultNotFound from '@/views/errorPages/ResultNotFound'

// auth pages
import ForgotPasswordPage from '@/views/authPages/ForgotPasswordPage'
import LoginPage from '@/views/authPages/LoginPage'
import ResetPasswordPage from '@/views/authPages/ResetPasswordPage'

// dummy
import DummyPage from '@/views/DummyPage'

/* general base routes */
const baseRoutes = [
  {
    path: '/:lang',
    name: 'dashboard',
    component: Language,
    meta: {
      body: {
        landing: 'page_landing_page meta',
        dashboard: 'page_dashboard',
      },
    },
  },
  /* dummy page only for development */
  {
    path: '/:lang/dummy',
    name: 'dummy',
    component: DummyPage,
    meta: { requiresAuth: true, bodyClass: 'page_dummy' },
  },
  /* dummy routes end */
  /* auth routes start */
  {
    path: '/:lang/login',
    name: 'login',
    component: LoginPage,
    meta: { requiresAnon: true, bodyClass: 'page_login meta' },
    beforeEnter: (to, from, next) => {
      Object.assign(to.query, from.query)
      next()
    },
  },
  {
    path: '/:lang/logout',
    name: 'logout',
    beforeEnter: (to, from, next) => {
      store.dispatch('auth/logout').then(() => {
        next({ path: `/${to.params.lang}/login` })
      })
    },
  },
  {
    path: '/:lang/forgot_password',
    name: 'forgot-password',
    component: ForgotPasswordPage,
    meta: { requiresAnon: true, bodyClass: 'page_forgot-password meta' },
  },
  {
    path: '/:lang/reset_password',
    name: 'reset-password',
    component: ResetPasswordPage,
    meta: { requiresAnon: false, bodyClass: 'page_reset_password meta' },
  },
  /* auth routes end */
  {
    path: '/:lang/activity_feed',
    name: 'activity-feed',
    component: ActivityFeedPage,
    meta: { requiresAuth: true, bodyClass: 'page_activity_feed' },
  },
  {
    path: '/:lang/categories/:category_id',
    name: 'category',
    component: GroupPage,
    meta: { requiresAuth: true, bodyClass: 'page_group' },
  },
  {
    path: '/:lang/groups',
    name: 'groups',
    component: GroupsPage,
    meta: { requiresAuth: true, bodyClass: 'page_groups' },
  },
  {
    path: '/:lang/groups/:id',
    name: 'group',
    component: GroupPage,
    meta: { requiresAuth: true, bodyClass: 'page_group' },
  },
  {
    path: '/:lang/groups/:id/topics/:topic_id',
    name: 'topic',
    redirect: to => {
      return {
        path: '/:lang/groups/:id',
        query: { ...to.query, topic_id: to.params.topic_id },
        hash: to.hash,
      }
    },
  },
  {
    path: '/:lang/locations',
    name: 'locations',
    component: HotelOverviewPage,
    meta: { requiresAuth: true, bodyClass: 'page_ratings' },
  },
  {
    path: '/:lang/mocs',
    name: 'mocs',
    meta: { requiresAuth: true },
    beforeEnter() {
      const loc = location || window.location
      loc.href = loc.origin + `${process.env.VUE_APP_API_URL}auth/mocs`
    },
  },
  {
    path: '/:lang/news/:id',
    name: 'news',
    component: NewsPage,
    meta: { requiresAuth: true, bodyClass: 'page_news' },
  },
  {
    path: '/:lang/occasions/',
    name: 'occasions',
    component: OccasionsPage,
    meta: { requiresAuth: true, bodyClass: 'page_occasions' },
  },
  {
    path: '/:lang/page/:id',
    name: 'page',
    component: CMSPage,
    meta: { requiresAnon: false, bodyClass: 'page_cms' },
  },
  {
    path: '/:lang/previews/:id',
    name: 'page-preview',
    component: CMSPreview,
    meta: { requiresAnon: false, bodyClass: 'page_cms' },
  },
  {
    path: '/:lang/ratings',
    name: 'ratings',
    component: RatingsPage,
    meta: { requiresAuth: true, bodyClass: 'page_ratings' },
  },
  {
    path: '/:lang/ratings/:id',
    name: 'rating',
    component: RatingPage,
    meta: { requiresAuth: true, bodyClass: 'page_rating' },
  },
  {
    path: '/:lang/search_results',
    name: 'search-results',
    component: SearchResultsPage,
    meta: { requiresAuth: true, bodyClass: 'page_search_results' },
  },
  {
    path: '/:lang/who_is_who',
    name: 'who-is-who',
    component: WhoIsWhoPage,
    meta: { requiresAuth: true, bodyClass: 'page_who-is-who' },
  },
  {
    path: '/:lang/who_is_who/:id',
    name: 'profile',
    component: ProfilePage,
    meta: { requiresAuth: true, bodyClass: 'page_profile' },
  },
  /* error pages start */
  {
    path: '/:lang/403',
    name: '403',
    component: AccessDenied,
    meta: { requiresAuth: false, bodyClass: 'page_permission-denied' },
  },
  {
    path: '/:lang/404',
    name: '404',
    component: PageNotFound,
    meta: { requiresAuth: false, bodyClass: 'page_not-found' },
  },
  {
    path: '/:lang/error',
    name: 'error',
    component: GeneralError,
    meta: { requiresAuth: false, bodyClass: 'page_error' },
  },
  {
    path: '/:lang/result-not-found',
    name: 'result-not-found',
    component: ResultNotFound,
    meta: { requiresAuth: true, bodyClass: 'page_result-not-found' },
  },
  /* END error pages */
  /* routes regarding to customer config */
  {
    path: '/:lang/page/...pages.terms',
    alias: ['/:lang/terms'],
    name: 'Terms',
    redirect: {},
    meta: { requiresAuth: true, bodyClass: 'page_terms' },
  },
  {
    path: '/:lang/page/...pages.q_and_a',
    alias: ['/:lang/q-and-a'],
    name: 'Q&A',
    redirect: {},
    meta: { requiresAuth: true, bodyClass: 'page_qa' },
  },
  {
    path: '/:lang/page/...pages.imprint',
    alias: ['/:lang/imprint'],
    name: 'Imprint',
    redirect: {},
    meta: { requiresAuth: true, bodyClass: 'page_imprint' },
  },
  {
    path: '/:lang/page/...menu.jobs',
    alias: ['/:lang/jobs'],
    name: 'Jobs',
    redirect: {},
    meta: { requiresAuth: true, bodyClass: 'page_jobs' },
  },
  /* END routes regarding to customer config */
]

/* final routes, separated since they has to come at last */
baseRoutes.push(
  {
    path: '/:lang/*',
    name: 'pageNotFound',
    redirect: '/:lang/404',
    meta: { requiresAnon: false },
  },
)

export default baseRoutes
